import { FilterFn } from '@tanstack/react-table';

import { FilterType } from '@/cip/_openapi/portfolio-optimizer';

/**
 * Client-side TanStack `FilterFn`s that evaluate the same filter-value shapes produced
 * by `ColumnFilter.tsx`'s popovers. Use these when a table filters in-memory
 * (`ClientDataTable`); `ServerDataTable` bypasses these and
 * forward the same shapes to the backend via `processFilter` instead.
 *
 * Value shapes per filter type (must stay in sync with `ColumnFilter.tsx`):
 *  - STRING:  `[searchTerm]` (single-element array) or plain string
 *  - LIST:    `string[]` of allowed values
 *  - NUMBER:  `{ eq?, gte?, lte?, mode? }` (strings or numbers; `eq` wins if set)
 *  - DATE:    `{ from?, to? }` (ISO date strings)
 *  - BOOLEAN: `boolean`
 */

const textFilterFn: FilterFn<any> = (row, columnId, value: string[] | string | undefined) => {
    if (!value) return true;
    const term = Array.isArray(value) ? value[0] : value;
    if (!term) return true;
    const cellValue = row.getValue(columnId);
    return String(cellValue ?? '')
        .toLowerCase()
        .includes(String(term).toLowerCase());
};

const listFilterFn: FilterFn<any> = (row, columnId, value: string[] | undefined) => {
    if (!value || value.length === 0) return true;
    const cellValue = row.getValue(columnId);
    if (cellValue == null) return false;
    return value.includes(String(cellValue));
};

const numberRangeFilterFn: FilterFn<any> = (
    row,
    columnId,
    value: { eq?: string | number; gte?: string | number; lte?: string | number } | undefined,
) => {
    if (!value) return true;
    const raw = row.getValue(columnId);
    if (raw == null || raw === '') return false;
    const n = Number(raw);
    if (Number.isNaN(n)) return false;
    if (value.eq != null && value.eq !== '') {
        return n === Number(value.eq);
    }
    if (value.gte != null && value.gte !== '' && n < Number(value.gte)) return false;
    if (value.lte != null && value.lte !== '' && n > Number(value.lte)) return false;
    return true;
};

const dateRangeFilterFn: FilterFn<any> = (row, columnId, value: { from?: string; to?: string } | undefined) => {
    if (!value || (!value.from && !value.to)) return true;
    const raw = row.getValue(columnId);
    if (raw == null) return false;
    const t = new Date(raw as any).getTime();
    if (Number.isNaN(t)) return false;
    if (value.from && t < new Date(value.from).getTime()) return false;
    if (value.to && t > new Date(value.to).getTime()) return false;
    return true;
};

const booleanFilterFn: FilterFn<any> = (row, columnId, value: boolean | undefined) => {
    if (value === undefined) return true;
    return Boolean(row.getValue(columnId)) === Boolean(value);
};

/** Map from `meta.filterType` to the matching client-side TanStack filterFn. */
export const CLIENT_FILTER_FN_FOR_TYPE: Record<FilterType, FilterFn<any>> = {
    [FilterType.STRING]: textFilterFn,
    [FilterType.LIST]: listFilterFn,
    [FilterType.NUMBER]: numberRangeFilterFn,
    [FilterType.DATE]: dateRangeFilterFn,
    [FilterType.BOOLEAN]: booleanFilterFn,
};
